import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Input, DatePicker, Tabs, Popover, Button as AntButton } from 'antd';
import { FilterFilled } from '@ant-design/icons';
import { Box, Flex } from 'atoms';
import { getPoRList } from 'store/approvalSlice';
import { RCTable, PoRViewerModal } from 'components';
import { formatApprovalDate, formatApprovalTime } from 'utils/approvalDateTime';

const { RangePicker } = DatePicker;
const { Search } = Input;

const DateFormat = 'DD-MM-YYYY';
const TIME_FORMAT = 'hh:mm a';

// API `status` is lowercase; these tabs drive the `status` filter directly.
const STATUS_TABS = [
	{ key: 'pending', label: 'Pending' },
	{ key: 'approved', label: 'Approved' },
	{ key: 'rejected', label: 'Rejected' },
];

const STATUS_LABEL = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };

const getStatusColor = (status) => {
	switch (status) {
		case 'approved':
			return '#34C759';
		case 'pending':
			return '#FF9500';
		case 'rejected':
			return '#FF3B30';
		default:
			return '#000';
	}
};

const LtSearchBar = styled(Search)`
	border: none;
`;

export default function PoRTaskForm() {
	const [porData, setPorData] = useState({ rows: [], count: 0 });
	const [paginate, setPaginate] = useState({ limit: 10, offset: 0, currentPage: 1 });
	const [statusTab, setStatusTab] = useState('pending');
	const [searchInput, setSearchInput] = useState(undefined); // what the user is typing
	const [searchStr, setSearchStr] = useState(undefined); // committed free-text query
	const [tripId, setTripId] = useState(undefined); // exact trip id (filter popover)
	const [dateRange, setDateRange] = useState([]);
	const [filterOpen, setFilterOpen] = useState(false);

	// RCTable dispatch action — memoized on the active filters so a change
	// re-triggers the fetch. Only whitelisted keys reach the API.
	const dispatchAction = useCallback(
		(params) =>
			getPoRList({
				limit: params.limit || 10,
				offset: params.offset || 0,
				status: statusTab || undefined,
				query_str: searchStr || undefined,
				trip_id: tripId ? Number(tripId) : undefined,
				created_from: dateRange?.[0] ? dateRange[0].toISOString() : undefined,
				created_to: dateRange?.[1] ? dateRange[1].toISOString() : undefined,
			}),
		[statusTab, searchStr, tripId, dateRange]
	);

	// Reset to first page (and force a refetch) whenever filters change.
	const resetPage = () => setPaginate((p) => ({ ...p, offset: 0, currentPage: 1 }));
	const reload = () => setPaginate((p) => ({ ...p }));

	const onTabChange = (key) => {
		setStatusTab(key);
		resetPage();
	};

	const renderDateTime = (value) => (
		<Box>
			<Box style={{ fontSize: '12px', color: '#000' }}>
				{formatApprovalDate(value, DateFormat)}
			</Box>
			<Box style={{ fontSize: '12px', color: '#787272' }}>
				{formatApprovalTime(value, TIME_FORMAT)}
			</Box>
		</Box>
	);

	const columns = [
		{
			title: 'Trip ID',
			dataIndex: 'trip_id',
			key: 'trip_id',
			render: (v, item) => (
				<span style={{ color: '#1cbdf6', fontWeight: 700 }}>
					{item?.trip_id
						? `IRPL-${item.trip_id}`
						: item?.trip_id || item?.display_id || item?.trip_code || '-'}
				</span>
			),
		},
		{ title: 'Vendor', dataIndex: 'vendor_name', key: 'vendor_name' },
		{ title: 'Vehicle', dataIndex: 'vehicle_number', key: 'vehicle_number' },
		{ title: 'Project', dataIndex: 'project_name', key: 'project_name' },
		{ title: 'Submitted By', dataIndex: 'submitted_by', key: 'submitted_by' },
		{
			title: 'Submitted At',
			dataIndex: 'submitted_at',
			key: 'submitted_at',
			render: renderDateTime,
		},
		{
			title: 'Submission #',
			dataIndex: 'submission_number',
			key: 'submission_number',
			align: 'center',
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (v) => (
				<span style={{ color: getStatusColor(v), fontWeight: 600 }}>
					{STATUS_LABEL[v] || v || '-'}
				</span>
			),
		},
		{
			title: 'Actions',
			key: 'action',
			render: (_, item) => (
				<PoRViewerModal
					porId={item.por_id}
					tripId={item.trip_id}
					documentKey={item.document_key}
					onActionComplete={reload}
					data={{
						display_id: item?.display_id,
						trip_code: item?.trip_code,
						project_name: item?.project_name,
						vendor_name: item?.vendor_name,
						vehicle_number: item?.vehicle_number,
						status: item?.status,
						submission_number: item?.submission_number,
						rejection_reason: item?.rejection_reason,
					}}
				/>
			),
		},
	];

	const clearFilter = () => {
		setTripId(undefined);
		setDateRange([]);
		setFilterOpen(false);
		resetPage();
	};

	const filterContent = (
		<Box style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '260px' }}>
			<Input
				placeholder="Trip ID"
				allowClear
				value={tripId}
				onChange={(e) => setTripId(e.target.value || undefined)}
			/>
			<RangePicker
				style={{ width: '100%' }}
				value={dateRange}
				onChange={(val) => setDateRange(val || [])}
				placeholder={['Submitted from', 'Submitted to']}
			/>
			<Flex justifyContent="space-between">
				<AntButton size="small" onClick={clearFilter}>
					Clear
				</AntButton>
				<AntButton
					size="small"
					type="primary"
					onClick={() => {
						setFilterOpen(false);
						resetPage();
					}}
				>
					Apply
				</AntButton>
			</Flex>
		</Box>
	);

	return (
		<Box>
			<Flex justifyContent="space-between" alignItems="center" mt={3}>
				<Box flex="0 0 30%">
					<LtSearchBar
						placeholder="Search Trip ID / Vendor / Project"
						enterButton="Search"
						value={searchInput}
						allowClear
						onChange={(e) => {
							const v = e.target.value;
							setSearchInput(v);
							// Reset the search when the box is cleared.
							if (!v) {
								setSearchStr(undefined);
								resetPage();
							}
						}}
						onSearch={(v) => {
							setSearchStr(v || undefined);
							resetPage();
						}}
					/>
				</Box>
				<Flex alignItems="center" style={{ gap: '16px' }}>
					<Popover
						open={filterOpen}
						onOpenChange={setFilterOpen}
						trigger="click"
						placement="bottomRight"
						content={filterContent}
					>
						<button
							type="button"
							style={{
								display: 'flex',
								alignItems: 'center',
								color: 'black',
								backgroundColor: 'transparent',
								borderRadius: '4px',
								padding: '3px 9px',
								border: '1px solid #C6C6C8',
								cursor: 'pointer',
							}}
						>
							<FilterFilled style={{ marginRight: '3px' }} /> Filter
						</button>
					</Popover>
					<Box>Total Items: {porData?.count || 0}</Box>
				</Flex>
			</Flex>

			<Tabs activeKey={statusTab} onChange={onTabChange} items={STATUS_TABS} />

			<RCTable
				columns={columns}
				dispatchAction={dispatchAction}
				tableDatas={[porData, setPorData]}
				togglePaginate={[paginate, setPaginate]}
				rowKey="por_id"
				scroll={{ x: true }}
				locale={{ emptyText: 'No Data' }}
			/>
		</Box>
	);
}

PoRTaskForm.propTypes = {};
